import React, { useEffect, useState } from "react";
import { Users, Building2, FileText, Share2, TrendingUp, Clock, ArrowRightLeft, MessageSquarePlus } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";

export default function AdminAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [feedback, setFeedback] = useState([]);
  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setData(data)).catch(() => {});
    api.get("/feedback").then(({ data }) => setFeedback(data)).catch(() => setFeedback([]));
  }, []);
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  if (!data) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <header>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Admin Analytics</h1>
        <p className="text-muted-foreground text-sm mt-1">Metriche di adozione, produttività e conversione. Aggiornato al {new Date(data.as_of).toLocaleString("it-IT")}.</p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Users} label="Utenti totali" value={data.users_total} />
        <KPI icon={Users} label="Attivi 30g" value={data.active_users_30d} accent="text-emerald-600" />
        <KPI icon={Building2} label="Immobili 30g" value={data.properties_added_30d} />
        <KPI icon={FileText} label="Documenti 30g" value={data.docs_uploaded_30d} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border/60 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Share2 className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-medium">Deal Room</h3>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <BigStat label="Aperte" value={data.deal_rooms.total} />
            <BigStat label="Ultimi 30g" value={data.deal_rooms.last_30d} />
            <BigStat label="Interest rate" value={`${data.deal_rooms.interest_rate_pct}%`} accent="text-emerald-600" />
          </div>
        </Card>

        <Card className="border-border/60 p-6">
          <div className="flex items-center gap-2 mb-4">
            <ArrowRightLeft className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-medium">Conversione funnel</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <BigStat label="Opportunità → Trattativa" value={`${data.conversion.opp_to_deal_pct}%`} />
            <BigStat label="Trattativa → Accordo" value={`${data.conversion.deal_to_agreement_pct}%`} accent="text-emerald-600" />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-border/60 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-medium">Tempo medio di risposta</h3>
          </div>
          <div className="font-heading text-5xl font-bold tabular-nums">{data.avg_response_hours}<span className="text-lg text-muted-foreground"> ore</span></div>
          <p className="text-xs text-muted-foreground mt-2">Dalla domanda del visitatore Deal Room alla prima risposta host.</p>
        </Card>

        <Card className="border-border/60 p-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquarePlus className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-medium">Feedback beta</h3>
            <Badge variant="secondary" className="ml-auto">{feedback.length}</Badge>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin">
            {feedback.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Nessun feedback ancora.</p>}
            {feedback.slice(0, 10).map((f) => (
              <div key={f.id} className="p-2 border border-border/60 rounded-md" data-testid={`fb-item-${f.id}`}>
                <div className="flex items-center gap-2 text-xs">
                  <Badge className={`text-[10px] ${f.priority === "blocker" || f.priority === "high" ? "bg-rose-100 text-rose-700" : f.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"} border-transparent`}>{f.priority}</Badge>
                  <span className="text-muted-foreground truncate">{f.user_name || f.user_email}</span>
                  <span className="text-muted-foreground ml-auto tabular-nums">{new Date(f.created_at).toLocaleDateString("it-IT")}</span>
                </div>
                <div className="text-sm mt-1 line-clamp-2">{f.description}</div>
                <div className="text-[10px] text-muted-foreground mt-1 truncate">📍 {f.page}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent }) {
  return (
    <Card className="border-border/60 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className={`mt-2 font-heading text-3xl font-semibold tabular-nums ${accent || ""}`}>{value ?? "—"}</p>
        </div>
        <div className="w-9 h-9 rounded-md bg-muted flex items-center justify-center text-primary"><Icon className="w-4 h-4" /></div>
      </div>
    </Card>
  );
}
function BigStat({ label, value, accent }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className={`font-heading text-2xl font-semibold tabular-nums mt-1 ${accent || ""}`}>{value ?? "—"}</div></div>;
}
